const TOAST_MAX_STACK = 3;
const TOAST_DEFAULT_DURATION = 4500;
const TOAST_EXIT_DURATION = 250;

function generateToastId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }

    return `toast-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function normalizeToastPayload(payload = {}) {
    return {
        id: typeof payload.id === "string" && payload.id ? payload.id : generateToastId(),
        title: payload.title ?? "Success",
        message: payload.message ?? payload.body ?? "",
        type: payload.type ?? payload.variant ?? "success",
        animation: payload.animation ?? "slide",
        duration: Number.isFinite(payload.duration)
            ? Math.max(0, Number(payload.duration))
            : TOAST_DEFAULT_DURATION,
        visible: true,
    };
}

function makeToastStore(Alpine) {
    return {
        items: [],
        get visibleCount() {
            return this.items.reduce((count, toast) => count + (toast.visible ? 1 : 0), 0);
        },
        push(payload = {}) {
            const payloadNormalized = normalizeToastPayload(payload);
            const toast = typeof Alpine !== "undefined" && typeof Alpine.reactive === "function"
                ? Alpine.reactive(payloadNormalized)
                : payloadNormalized;
            const existingIndex = this.items.findIndex(({ id }) => id === toast.id);

            toast.visible = false;

            if (existingIndex !== -1) {
                const previous = this.items[existingIndex];
                this.clearTimeouts(previous);
                this.items.splice(existingIndex, 1, toast);
            } else {
                this.items.push(toast);
            }

            const reveal = () => {
                toast.visible = true;
            };

            if (typeof window.requestAnimationFrame === "function") {
                window.requestAnimationFrame(() => window.requestAnimationFrame(reveal));
            } else {
                window.setTimeout(reveal, 16);
            }

            this.truncate();
            this.queueAutoDismiss(toast);

            return toast.id;
        },
        dismiss(id) {
            const toast = this.items.find((item) => item.id === id);
            if (!toast) {
                return;
            }

            this.clearTimeouts(toast);

            if (!toast.visible) {
                this.removeToast(id);
                return;
            }

            toast.visible = false;
            toast.exitTimer = window.setTimeout(() => {
                this.removeToast(id);
            }, TOAST_EXIT_DURATION);
        },
        clearTimeouts(toast) {
            if (toast?.timeout) {
                window.clearTimeout(toast.timeout);
                toast.timeout = undefined;
            }

            if (toast?.exitTimer) {
                window.clearTimeout(toast.exitTimer);
                toast.exitTimer = undefined;
            }
        },
        queueAutoDismiss(toast) {
            if (!toast || toast.duration <= 0) {
                return;
            }

            toast.timeout = window.setTimeout(() => {
                this.dismiss(toast.id);
            }, toast.duration);
        },
        truncate() {
            while (this.visibleCount > TOAST_MAX_STACK) {
                const oldest = this.items.find((item) => item.visible);
                if (!oldest) {
                    break;
                }

                this.dismiss(oldest.id);
            }
        },
        clearAll() {
            this.items.slice().forEach((toast) => this.dismiss(toast.id));
        },
        removeToast(id) {
            const index = this.items.findIndex((item) => item.id === id);
            if (index === -1) {
                return;
            }

            const [toast] = this.items.splice(index, 1);
            this.clearTimeouts(toast);
        },
    };
}

export function registerToastStore(Alpine) {
    document.addEventListener("alpine:init", () => {
        Alpine.store("toast", makeToastStore(Alpine));
    });
}
